import { Injectable } from '@angular/core';

import { Address } from '@guardfinder/address';
import { AnalyticsService } from '@guardfinder/analytics';

import { Observable, tap } from 'rxjs';

import { ApiClient } from '../../../api-client';
import { UnitResult } from '../../../error/Domain/unit-result';
import { AddressVerifiedEvent } from '../../Domain/Event/address-verified.event';
import { BackendGuardsAvailableService } from './backend-guards-available.service';

/**
 * AnalyticsGuardsAvailableServiceDecorator
 *
 * A decorator for BackendGuardsAvailableService that adds analytics tracking functionality.
 * This service extends the base guard availability check by logging analytics events
 * when address verification is performed.
 *
 * Responsibilities:
 * - Performs the same guard availability check as BackendGuardsAvailableService
 * - Logs an AddressVerifiedEvent to the analytics system after each check
 * - Preserves the original return value and error handling from the base service
 *
 * Design Pattern:
 * - Implements the Decorator pattern to add analytics functionality without modifying
 *   the original service implementation
 *
 * Dependencies:
 * - AnalyticsService: Used to log custom analytics events
 * - ApiClient: Passed to the parent BackendGuardsAvailableService
 *
 * Usage:
 * - Used in place of BackendGuardsAvailableService when analytics tracking is required
 * - Automatically used through dependency injection when this service is provided
 */
@Injectable({
    providedIn: 'root'
})
export class AnalyticsGuardsAvailableServiceDecorator extends BackendGuardsAvailableService {
    /**
     * Creates an instance of AnalyticsGuardsAvailableServiceDecorator.
     *
     * @param analytics - The analytics service used to log custom events
     * @param apiClient - The API client passed to the parent BackendGuardsAvailableService
     */
    constructor(
        private readonly analytics: AnalyticsService,
        apiClient: ApiClient
    ) {
        super(apiClient);
    }

    /**
     * Checks if guards are available at the specified address and logs an analytics event
     * with the verification result.
     *
     * This method extends the base implementation by adding analytics tracking:
     * 1. Calls the parent service's execute method to check guard availability
     * 2. Creates and logs an AddressVerifiedEvent with the success status of the result
     *
     * @param clientLead - The address to check for guard availability
     * @returns An Observable of UnitResult<boolean> with the same behavior as the parent service:
     *          - true indicates guards are available at the location
     *          - false indicates no guards are available
     *          - UnitResult.failure indicates an error occurred during the check
     */
    override execute(clientLead: Address): Observable<UnitResult<boolean>> {
        return super.execute(clientLead).pipe(
            tap(result => {
                if (result.isError()) return;

                const event = new AddressVerifiedEvent(result.getResult());
                this.analytics.logCustomEvent(event).then();
            })
        );
    }
}
